import type { JSX } from 'react';
import { ShouldConfirmBeforeExitFormProps } from 'dg-web-shared/common/utils/FormDefinitions.tsx';
import { ParkingaboUser } from './OperatorParkingaboUsersForm.tsx';
import {
    RequestStatus,
    ServerWriteState,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { ValidationData } from 'dg-web-shared/lib/forms/FormValidationHelpers.tsx';
import { Message } from 'dg-web-shared/lib/Localized.ts';
import { Box, InputLabel, Switch, TextField, Tooltip } from '@mui/material';
import { AssignmentInd, Person } from '@mui/icons-material';
import {
    Localized,
    useLanguage,
    useLocalize,
} from 'dg-web-shared/lib/Localization.tsx';
import { getCustomerDisplayName } from 'dg-web-shared/common/models/Users.tsx';
import { DateTime, Duration } from 'luxon';
import { ensureIsoDate } from 'dg-web-shared/lib/Date';
import { useEasyForm } from 'dg-web-shared/common/utils/FormHooksUtils.tsx';
import { useOutletContext } from 'react-router';
import { useQuery } from '../shared-mui-components/filter/OperatorFilterHelpers.tsx';
import { intervalDurationToStartSelectOptions } from 'product-shared/product-template/SubscriptionIntervalDuration.ts';
import { ParkingaboUsersProductsAddContextType } from './OperatorParkingaboUsersProducts.tsx';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import {
    Control,
    Controller,
    FieldValues,
    Path,
    useForm,
} from 'react-hook-form';
import {
    FormContentLayout,
    FormLeftColumn,
    FormRightColumn,
} from '../layout/components/form/FormLayout.tsx';
import InfoIcon from '@mui/icons-material/Info';
import { Language } from 'dg-web-shared/lib/Text.ts';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { MaterialBar } from '../layout/components/MaterialBar.tsx';
import { OperatorFormSelect } from '../ui/form/OperatorFormSelect.tsx';
import {
    FreeOfChargeProductTemplateConfig,
    isRangedVehicleLimit,
    MaxConcurrentVehiclesLimitConfig,
    ProductTemplateConfig,
    ProductTemplateConfigType,
    SubscriptionConfig,
} from 'product-shared/product-template/ProductTemplate.ts';
import { HeaderComponent } from '../layout/components/HeaderComponent.tsx';
import { HeaderCancelSaveButtons } from '../layout/components/HeaderCancelSaveButtons.tsx';
import { OperatorRoutedModalContent } from '../layout/components/OperatorRoutedModalContent.tsx';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../ui/modal/OperatorRoutedModal.tsx';
import { GenericFormSubmitError } from './ParkingaboUsersUtils.tsx';
import { ProductTemplateZoneDescriptions } from '../product-types/OperatorProductTypesDetailModal.tsx';
import {
    FixedDuration,
    FixedDurationName,
    fixedDurationTextByName,
    getDefaultDurationIfApplicable,
    mapFixedDurationNameToValue,
    mapFixedDurationValueToName,
} from 'product-shared/product-template/FixedDuration.tsx';
import { ReadOnlyTextField } from '../ui/material-ui/ReadOnlyTextField.tsx';
import { mandatoryFieldErrorMessage } from 'dg-web-shared/common/components/translations/FormValidationErrorTexts.ts';
import { ZoneType } from 'dg-web-shared/dto/ZoneType.ts';

export function OperatorParkingaboUsersProductsAdd() {
    const {
        parkingaboUser,
        purchasableProductTemplates,
        refetchProducts,
        refetchParkingaboUser,
    } = useOutletContext<ParkingaboUsersProductsAddContextType>();

    const query = useQuery();
    const selfUrl = `..?${query.toString()}`;

    return (
        <OperatorRoutedModal
            variant={ModalVariant.STANDARD}
            backUrl={selfUrl}
            render={controller => (
                <OperatorParkingaboUsersProductsAddContent
                    onCreated={() => {
                        controller.setShouldConfirmBeforeLeave(false);
                        refetchProducts();
                        controller.safeNavigateTo(selfUrl);
                        refetchParkingaboUser();
                    }}
                    parkingaboUser={parkingaboUser}
                    productTemplates={purchasableProductTemplates}
                    onClose={controller.close}
                    onDirtyStateChange={controller.setShouldConfirmBeforeLeave}
                />
            )}
        />
    );
}

function useProductSubmitRequest(
    onCreated: () => void,
): ServerWriteState<null, ValidationData, CreateProductPayload> {
    const [productAddRequestState, submitProductAddRequest, setState] =
        useServerWrite<CreateProductPayload, null, ValidationData>(payload => ({
            url: `/ui-api/operator-account/parkingabo/customer/${payload.customerNr}/product`,
        }));

    useServerSuccessEffect(productAddRequestState, onCreated);

    return [productAddRequestState, submitProductAddRequest, setState];
}

interface OperatorParkingaboUsersProductsAddProps extends ShouldConfirmBeforeExitFormProps {
    parkingaboUser: ParkingaboUser;
    productTemplates: ProductsAddTemplateItem[];
    onCreated: () => void;
}

function OperatorParkingaboUsersProductTemplateSwitch({
    productTemplates,
    render,
}: {
    productTemplates: ProductsAddTemplateItem[];
    render: (
        productTemplateDropdown: JSX.Element,
        selectedProductTemplate: ProductsAddTemplateItem,
    ) => JSX.Element;
}) {
    const localize = useLocalize();
    const form = useForm<ContractTemplateIdValue>({
        defaultValues: {
            [ContractTemplateIdField.contractTemplateId]:
                productTemplates[0].contractTemplateId,
        },
    });
    const contractTemplateId = form.watch(
        ContractTemplateIdField.contractTemplateId,
    );
    const selectedProductTemplate = productTemplates.find(
        productTemplate =>
            productTemplate.contractTemplateId == contractTemplateId,
    );
    if (selectedProductTemplate == undefined) {
        throw new Error('Product not inside selectable Dropdown');
    }
    return render(
        <OperatorFormSelect
            readonly={productTemplates.length < 2}
            label={
                <Localized
                    de="Produkt-Typ"
                    fr="Type de produit"
                    it="Tipo prodotto"
                    en="Product type"
                />
            }
            name={ContractTemplateIdField.contractTemplateId}
            control={form.control}
            options={productTemplates.map(productTemplate => ({
                display: localize(productTemplate.productConfig.productName),
                value: productTemplate.contractTemplateId,
            }))}
        />,
        selectedProductTemplate,
    );
}

export function OperatorParkingaboUsersProductsAddContent({
    parkingaboUser,
    productTemplates,
    onClose,
    onDirtyStateChange,
    onCreated,
}: OperatorParkingaboUsersProductsAddProps) {
    return (
        <OperatorParkingaboUsersProductTemplateSwitch
            productTemplates={productTemplates}
            render={(productTemplateDropdown, selectedProductTemplate) => {
                switch (selectedProductTemplate.productConfig.configType) {
                    case ProductTemplateConfigType.SUBSCRIPTION:
                        return (
                            <OperatorParkingaboUsersProductsAddSubscription
                                key={selectedProductTemplate.contractTemplateId}
                                parkingaboUser={parkingaboUser}
                                onDirtyStateChange={onDirtyStateChange}
                                productTemplateDropdown={
                                    productTemplateDropdown
                                }
                                onClose={onClose}
                                onCreated={onCreated}
                                subscriptionConfig={
                                    selectedProductTemplate.productConfig
                                }
                                zonesDescriptions={
                                    selectedProductTemplate.zonesDescriptions
                                }
                                contractTemplateId={
                                    selectedProductTemplate.contractTemplateId
                                }
                            />
                        );
                    case ProductTemplateConfigType.ALL_ACCESS:
                    case ProductTemplateConfigType.PERMIT_UNTIL_REVOCATION:
                    case ProductTemplateConfigType.PERMIT_FREE_FIXED_DURATION:
                    case ProductTemplateConfigType.PERMIT_FREE_VALID_TO:
                        return (
                            <OperatorParkingaboUsersAddProductFreeOfCharge
                                key={selectedProductTemplate.contractTemplateId}
                                parkingaboUser={parkingaboUser}
                                onDirtyStateChange={onDirtyStateChange}
                                productTemplateDropdown={
                                    productTemplateDropdown
                                }
                                freeOfChargeProductConfig={
                                    selectedProductTemplate.productConfig
                                }
                                zonesDescriptions={
                                    selectedProductTemplate.zonesDescriptions
                                }
                                contractTemplateId={
                                    selectedProductTemplate.contractTemplateId
                                }
                                onClose={onClose}
                                onCreated={onCreated}
                            />
                        );
                    case ProductTemplateConfigType.GUEST_PERMIT_VALID_TO:
                    case ProductTemplateConfigType.GUEST_RESERVATION:
                    case ProductTemplateConfigType.GUEST_RESERVATION_UBS:
                    case ProductTemplateConfigType.CALENDAR_DAY_RESERVATION:
                    case ProductTemplateConfigType.CUSTOMER_EXTERNAL:
                    case ProductTemplateConfigType.EVENT:
                    case ProductTemplateConfigType.GUEST_DAY_PASS:
                        throw new Error('Product type should not be possible');
                }
            }}
        />
    );
}

type ProductAddProps = {
    parkingaboUser: ParkingaboUser;
    onDirtyStateChange?: (shouldConfirm: boolean) => void;
    productTemplateDropdown: JSX.Element;
    onClose: () => void;
    onCreated: () => void;
    contractTemplateId: number;
    zonesDescriptions: string[];
};

function OperatorParkingaboUsersProductsAddSubscription({
    parkingaboUser,
    onDirtyStateChange,
    subscriptionConfig,
    onClose,
    productTemplateDropdown,
    onCreated,
    contractTemplateId,
    zonesDescriptions,
}: {
    subscriptionConfig: SubscriptionConfig;
} & ProductAddProps) {
    const { language } = useLanguage();
    const [productAddRequestState, submitProductAddRequest] =
        useProductSubmitRequest(onCreated);
    const {
        formInfo: { control, handleSubmit, watch },
        genericSubmitError,
    } = useEasyForm(onDirtyStateChange, productAddRequestState, language, {
        defaultValues: {
            [Fields.startDate]: '',
            [Fields.endDate]: null,
            [Fields.tenantRemark]: '',
            [Fields.enforcementInstructions]: null,
            [Fields.durationName]: null,
            [Fields.maxConcurrentVehicles]:
                getConcurrentVehiclesLimitConfig(subscriptionConfig)
                    ?.minVehicleSelection ?? null,
        },
    });
    const startDate = watch(Fields.startDate);

    function onSubmit(formData: FormFields) {
        submitProductAddRequest({
            ...formData,
            customerNr: parkingaboUser.customerNr,
            contractTemplateId: contractTemplateId,
            duration: null,
            endDate: null,
        });
    }

    return (
        <OperatorRoutedModalContent
            header={
                <OperatorParkingaboUsersProductsAddHeader
                    loading={
                        productAddRequestState.status === RequestStatus.PENDING
                    }
                    onClose={onClose}
                    saveDisabled={!startDate}
                    onSubmit={handleSubmit(onSubmit)}
                />
            }
            bar={
                <MaterialBar
                    title={getCustomerDisplayName(parkingaboUser, false)}
                    Icon={Person}
                />
            }
            body={
                <>
                    <GenericFormSubmitError
                        submitState={productAddRequestState}
                        error={genericSubmitError}
                    />
                    <OperatorParkingaboUsersProductsAddSubscriptionBody
                        productTemplateDropdown={productTemplateDropdown}
                        subscriptionConfig={subscriptionConfig}
                        zonesDescriptions={zonesDescriptions}
                        language={language}
                        control={control}
                        startDateField={Fields.startDate}
                        tenantRemarkField={Fields.tenantRemark}
                        enforcementInstructionsField={
                            Fields.enforcementInstructions
                        }
                        maxConcurrentVehiclesField={
                            Fields.maxConcurrentVehicles
                        }
                    />
                </>
            }
        />
    );
}

function OperatorParkingaboUsersAddProductFreeOfCharge({
    parkingaboUser,
    onDirtyStateChange,
    productTemplateDropdown,
    freeOfChargeProductConfig,
    onClose,
    onCreated,
    contractTemplateId,
    zonesDescriptions,
}: {
    freeOfChargeProductConfig: FreeOfChargeProductTemplateConfig;
} & ProductAddProps) {
    const { language } = useLanguage();
    const [productAddRequestState, submitProductAddRequest] =
        useProductSubmitRequest(onCreated);

    const {
        formInfo: { control, handleSubmit, watch },
        genericSubmitError,
    } = useEasyForm(onDirtyStateChange, productAddRequestState, language, {
        defaultValues: {
            [Fields.startDate]: ensureIsoDate(DateTime.now()),
            [Fields.endDate]: null,
            [Fields.tenantRemark]: '',
            [Fields.enforcementInstructions]: null,
            [Fields.durationName]: getDefaultDurationIfApplicable(
                freeOfChargeProductConfig,
            ),
            [Fields.maxConcurrentVehicles]:
                getConcurrentVehiclesLimitConfig(freeOfChargeProductConfig)
                    ?.minVehicleSelection ?? null,
        },
    });
    const startDate = watch(Fields.startDate);

    function onSubmit(formData: FormFields) {
        submitProductAddRequest({
            ...formData,
            customerNr: parkingaboUser.customerNr,
            contractTemplateId: contractTemplateId,
            duration: formData[Fields.durationName]
                ? mapFixedDurationNameToValue(formData[Fields.durationName])
                : null,
            endDate: formData[Fields.endDate]
                ? ensureIsoDate(formData[Fields.endDate]!)
                : null,
        });
    }

    return (
        <OperatorRoutedModalContent
            header={
                <OperatorParkingaboUsersProductsAddHeader
                    loading={
                        productAddRequestState.status === RequestStatus.PENDING
                    }
                    onClose={onClose}
                    saveDisabled={!startDate}
                    onSubmit={handleSubmit(onSubmit)}
                />
            }
            bar={
                <MaterialBar
                    title={getCustomerDisplayName(parkingaboUser, false)}
                    Icon={Person}
                />
            }
            body={
                <>
                    <GenericFormSubmitError
                        submitState={productAddRequestState}
                        error={genericSubmitError}
                    />
                    <OperatorParkingaboUsersAddProductFreeOfChargeBody
                        productTemplateDropdown={productTemplateDropdown}
                        freeOfChargeProductConfig={freeOfChargeProductConfig}
                        zonesDescriptions={zonesDescriptions}
                        control={control}
                        startDateField={Fields.startDate}
                        endDateField={Fields.endDate}
                        currentStartDate={startDate}
                        tenantRemarkField={Fields.tenantRemark}
                        enforcementInstructionsField={
                            Fields.enforcementInstructions
                        }
                        durationNameField={Fields.durationName}
                        maxConcurrentVehicles={Fields.maxConcurrentVehicles}
                    />
                </>
            }
        />
    );
}

function OperatorParkingaboUsersProductsAddHeader({
    loading,
    onClose,
    saveDisabled,
    onSubmit,
}: {
    loading: boolean;
    onClose: () => void;
    saveDisabled: boolean;
    onSubmit: () => void;
}) {
    return (
        <HeaderComponent
            Icon={AssignmentInd}
            title={
                <Localized
                    de="Neues Produkt"
                    fr="Nouveau produit"
                    it="Nuovo prodotto"
                    en="New product"
                />
            }
            editMode={true}
            buttons={
                <HeaderCancelSaveButtons
                    onCancel={onClose}
                    onSave={onSubmit}
                    loading={loading}
                    saveDisabled={saveDisabled}
                    noDiscardDialog={true}
                />
            }
        />
    );
}

function OperatorParkingaboUsersProductsAddSubscriptionBody<
    TFieldValues extends FieldValues,
>({
    subscriptionConfig,
    zonesDescriptions,
    language,
    control,
    startDateField,
    tenantRemarkField,
    enforcementInstructionsField,
    maxConcurrentVehiclesField,
    productTemplateDropdown,
}: {
    subscriptionConfig: SubscriptionConfig;
    zonesDescriptions: string[];
    language: Language;
    control: Control<TFieldValues>;
    startDateField: Path<TFieldValues>;
    tenantRemarkField: Path<TFieldValues>;
    enforcementInstructionsField: Path<TFieldValues>;
    maxConcurrentVehiclesField: Path<TFieldValues>;
    productTemplateDropdown: JSX.Element;
}) {
    const now = DateTime.now();
    const startOptions = Array.from(
        {
            length: subscriptionConfig.intervalConfig
                .maxIntervalsPurchaseAheadInFuture,
        },
        (_v, k) => k + 1,
    ).map(amount => {
        const option = intervalDurationToStartSelectOptions(
            now,
            amount,
            subscriptionConfig.intervalConfig.intervalDuration,
            language,
        );
        return {
            display: option.dateText,
            value: ensureIsoDate(option.startDate),
        };
    });
    return (
        <FormContentLayout>
            <FormLeftColumn>
                {productTemplateDropdown}
                <OperatorParkingaboUsersProductsVehicleLimit
                    config={subscriptionConfig}
                    control={control}
                    maxConcurrentVehiclesField={maxConcurrentVehiclesField}
                />
                <OperatorFormSelect
                    readonly={startOptions.length == 0}
                    label={
                        <Localized
                            de="Beginn"
                            fr="Début"
                            it="Inizio"
                            en="Start"
                        />
                    }
                    name={startDateField}
                    control={control}
                    options={startOptions}
                />
                <OperatorParkingaboUsersProductsAdditionalInfo
                    additionalInfo={subscriptionConfig.additionalInfo}
                />
                <ProductTemplateZoneDescriptions
                    zoneDescriptions={zonesDescriptions}
                    allowedZoneTypes={subscriptionConfig.allowedZoneTypes}
                />
            </FormLeftColumn>
            <OperatorParkingaboUsersProductsAddRightColumn
                control={control}
                tenantRemarkField={tenantRemarkField}
                enforcementInstructionsField={enforcementInstructionsField}
                allowedZoneTypes={subscriptionConfig.allowedZoneTypes}
                configType={subscriptionConfig.configType}
            />
        </FormContentLayout>
    );
}

function OperatorParkingaboUsersAddProductFreeOfChargeBody<
    TFieldValues extends FieldValues,
>({
    productTemplateDropdown,
    freeOfChargeProductConfig,
    zonesDescriptions,
    control,
    startDateField,
    endDateField,
    currentStartDate,
    tenantRemarkField,
    enforcementInstructionsField,
    durationNameField,
    maxConcurrentVehicles,
}: {
    productTemplateDropdown: JSX.Element;
    freeOfChargeProductConfig: FreeOfChargeProductTemplateConfig;
    zonesDescriptions: string[];
    control: Control<TFieldValues>;
    startDateField: Path<TFieldValues>;
    endDateField: Path<TFieldValues>;
    currentStartDate: string;
    tenantRemarkField: Path<TFieldValues>;
    enforcementInstructionsField: Path<TFieldValues>;
    durationNameField: Path<TFieldValues>;
    maxConcurrentVehicles: Path<TFieldValues>;
}) {
    const now = DateTime.now();
    const localize = useLocalize();
    return (
        <FormContentLayout>
            <FormLeftColumn>
                {productTemplateDropdown}
                <OperatorParkingaboUsersProductsVehicleLimit
                    config={freeOfChargeProductConfig}
                    control={control}
                    maxConcurrentVehiclesField={maxConcurrentVehicles}
                />
                <Controller
                    name={startDateField}
                    control={control}
                    render={({ field }) => (
                        <>
                            <InputLabel variant="standard">
                                {
                                    <Localized
                                        de="Beginn"
                                        fr="Début"
                                        it="Inizio"
                                        en="Start"
                                    />
                                }
                            </InputLabel>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginTop: '-8px !important',
                                    marginLeft: '-12px !important',
                                }}
                            >
                                <Switch
                                    checked={
                                        currentStartDate == ensureIsoDate(now)
                                    }
                                    onChange={(_e, checked) => {
                                        if (!checked) {
                                            field.onChange(
                                                ensureIsoDate(
                                                    now.plus(
                                                        Duration.fromISO('P1D'),
                                                    ),
                                                ),
                                            );
                                        } else {
                                            field.onChange(ensureIsoDate(now));
                                        }
                                    }}
                                />

                                {currentStartDate == ensureIsoDate(now) ? (
                                    <Box
                                        sx={{
                                            paddingLeft: '5px',
                                            paddingRight: '40px',
                                            color: theme =>
                                                theme.palette.blue.main,
                                        }}
                                    >
                                        <Localized
                                            de="Sofort"
                                            fr="Immédiatement"
                                            it="Subito"
                                            en="Immediately"
                                        />
                                    </Box>
                                ) : (
                                    <DesktopDatePicker
                                        format="dd.MM.yyyy"
                                        value={DateTime.fromISO(
                                            currentStartDate,
                                        )}
                                        minDate={DateTime.now()}
                                        disablePast={true}
                                        onChange={d =>
                                            field.onChange(
                                                d ? ensureIsoDate(d) : null,
                                            )
                                        }
                                        slotProps={{
                                            textField: {
                                                size: 'medium',
                                            },
                                        }}
                                    />
                                )}
                            </Box>
                        </>
                    )}
                />
                {freeOfChargeProductConfig.configType ===
                    ProductTemplateConfigType.PERMIT_FREE_VALID_TO && (
                    <Box>
                        <Controller
                            name={endDateField}
                            control={control}
                            render={({ field, fieldState }) => (
                                <DesktopDatePicker
                                    label={
                                        <Localized
                                            de="Ende"
                                            fr="Fin"
                                            it="Fine"
                                            en="End"
                                        />
                                    }
                                    value={field.value}
                                    onChange={field.onChange}
                                    minDate={DateTime.fromISO(currentStartDate)}
                                    maxDate={DateTime.fromISO(currentStartDate)
                                        .plus({ year: 5 })
                                        .minus({ day: 1 })}
                                    slotProps={{
                                        textField: {
                                            error: !!fieldState.error,
                                            helperText:
                                                fieldState.error?.message,
                                        },
                                    }}
                                />
                            )}
                            rules={{
                                required: {
                                    value: true,
                                    message: localize(
                                        mandatoryFieldErrorMessage,
                                    ),
                                },
                            }}
                        />
                    </Box>
                )}
                {freeOfChargeProductConfig.configType ===
                    ProductTemplateConfigType.PERMIT_FREE_FIXED_DURATION && (
                    <OperatorFormSelect
                        readonly={
                            freeOfChargeProductConfig.allowedDurations
                                .length === 1
                        }
                        label={
                            <Localized
                                de="Dauer"
                                fr="Durée"
                                it="Durata"
                                en="Duration"
                            />
                        }
                        name={durationNameField}
                        control={control}
                        options={freeOfChargeProductConfig.allowedDurations
                            .map(allowedDuration => {
                                const durationName =
                                    mapFixedDurationValueToName(
                                        allowedDuration,
                                    );
                                if (!durationName) {
                                    return null;
                                }
                                return {
                                    display: (
                                        <Localized
                                            {...fixedDurationTextByName(
                                                durationName,
                                            )}
                                        />
                                    ),
                                    value: durationName,
                                };
                            })
                            .filter(a => a != null)}
                    />
                )}
                <OperatorParkingaboUsersProductsAdditionalInfo
                    additionalInfo={freeOfChargeProductConfig.additionalInfo}
                />
                <ProductTemplateZoneDescriptions
                    zoneDescriptions={zonesDescriptions}
                    allowedZoneTypes={
                        freeOfChargeProductConfig.allowedZoneTypes
                    }
                />
            </FormLeftColumn>
            <OperatorParkingaboUsersProductsAddRightColumn
                control={control}
                tenantRemarkField={tenantRemarkField}
                enforcementInstructionsField={enforcementInstructionsField}
                allowedZoneTypes={freeOfChargeProductConfig.allowedZoneTypes}
                configType={freeOfChargeProductConfig.configType}
            />
        </FormContentLayout>
    );
}

function OperatorParkingaboUsersProductsVehicleLimit<
    TFieldValues extends FieldValues,
>({
    config,
    control,
    maxConcurrentVehiclesField,
}: {
    config: FreeOfChargeProductTemplateConfig | SubscriptionConfig;
    control: Control<TFieldValues>;
    maxConcurrentVehiclesField: Path<TFieldValues>;
}): JSX.Element {
    switch (config.configType) {
        case ProductTemplateConfigType.ALL_ACCESS:
            return <UnlimitedVehiclesReadOnlyTextField />;
        case ProductTemplateConfigType.PERMIT_UNTIL_REVOCATION:
        case ProductTemplateConfigType.PERMIT_FREE_FIXED_DURATION:
        case ProductTemplateConfigType.SUBSCRIPTION:
        case ProductTemplateConfigType.PERMIT_FREE_VALID_TO: {
            const vehiclesLimitConfig = config.maxConcurrentVehiclesLimitConfig;
            if (!isRangedVehicleLimit(vehiclesLimitConfig)) {
                return <UnlimitedVehiclesReadOnlyTextField />;
            } else {
                if (
                    vehiclesLimitConfig.minVehicleSelection ===
                    vehiclesLimitConfig.maxVehicleSelection
                ) {
                    return (
                        <ReadOnlyTextField
                            label={
                                <OperatorParkingaboUsersProductsConcurrentVehiclesLabel />
                            }
                            value={vehiclesLimitConfig.minVehicleSelection}
                        />
                    );
                }
                return (
                    <OperatorFormSelect
                        readonly={false}
                        label={
                            <OperatorParkingaboUsersProductsConcurrentVehiclesLabel />
                        }
                        name={maxConcurrentVehiclesField}
                        control={control}
                        options={Array.from(
                            {
                                length:
                                    vehiclesLimitConfig.maxVehicleSelection -
                                    vehiclesLimitConfig.minVehicleSelection +
                                    1,
                            },
                            (_, i) =>
                                vehiclesLimitConfig.minVehicleSelection + i,
                        ).map(number => ({
                            display: number,
                            value: number,
                        }))}
                    />
                );
            }
        }
    }
}

export function UnlimitedVehiclesReadOnlyTextField() {
    return (
        <ReadOnlyTextField
            label={<OperatorParkingaboUsersProductsConcurrentVehiclesLabel />}
            value={
                <Localized
                    de="Unbegrenzt"
                    fr="Illimité"
                    it="Illimitato"
                    en="Unlimited"
                />
            }
        />
    );
}

export function OperatorParkingaboUsersProductsConcurrentVehiclesLabel() {
    return (
        <>
            <Localized
                de="Anzahl Plätze"
                fr="Nombre de places"
                it="Numero posti"
                en="Number of spaces"
            />
            <Tooltip
                title={
                    <Localized
                        de="Die Anzahl der verfügbaren Plätze (Fahrzeuge, die gleichzeitig parkiert werden können) für diesen Produkt."
                        fr="Le nombre de places disponibles (véhicules pouvant être stationnés simultanément) pour ce produit."
                        it="Il numero di posti disponibili (veicoli che possono essere parcheggiati contemporaneamente) per questo prodotto."
                        en="The number of available spaces (vehicles that can be parked simultaneously) for this product."
                    />
                }
                placement="right"
            >
                <InfoIcon
                    sx={{
                        color: theme => theme.palette.blue.main,
                        marginLeft: 1,
                    }}
                />
            </Tooltip>
        </>
    );
}

function OperatorParkingaboUsersProductsAdditionalInfo({
    additionalInfo,
}: {
    additionalInfo: Message | null;
}) {
    const localize = useLocalize();
    return (
        <TextField
            multiline
            disabled
            label={
                <Localized
                    de="Zusatzinformationen"
                    fr="Informations complémentaires"
                    it="Informazioni supplementari"
                    en="Additional information"
                />
            }
            value={additionalInfo ? localize(additionalInfo) : EmDash}
        />
    );
}

function OperatorParkingaboUsersProductsAddRightColumn<
    TFieldValues extends FieldValues,
>({
    control,
    tenantRemarkField,
    enforcementInstructionsField,
    allowedZoneTypes,
    configType,
}: {
    control: Control<TFieldValues>;
    tenantRemarkField: Path<TFieldValues>;
    enforcementInstructionsField: Path<TFieldValues>;
    allowedZoneTypes: ZoneType[];
    configType: ProductTemplateConfigType;
}) {
    return (
        <FormRightColumn>
            <Controller
                name={tenantRemarkField}
                control={control}
                render={({ field }) => (
                    <TextField
                        multiline
                        minRows={3}
                        maxRows={3}
                        label={
                            <OperatorParkingaboUsersProductsTenantRemarkLabel />
                        }
                        value={field.value}
                        onChange={e => field.onChange(e)}
                    />
                )}
            />
            {productsHasEnforcementInstructions(
                allowedZoneTypes,
                configType,
            ) && (
                <Controller
                    name={enforcementInstructionsField}
                    control={control}
                    render={({ field }) => (
                        <TextField
                            multiline
                            minRows={2}
                            maxRows={2}
                            label={<ProductsEnforcementInstructionsLabel />}
                            value={field.value}
                            onChange={e => field.onChange(e)}
                        />
                    )}
                />
            )}
        </FormRightColumn>
    );
}

export function productsHasEnforcementInstructions(
    allowedZoneTypes: ZoneType[],
    configType: ProductTemplateConfigType,
): boolean {
    if (!allowedZoneTypes.includes(ZoneType.enforced)) {
        return false;
    }
    switch (configType) {
        case ProductTemplateConfigType.ALL_ACCESS:
        case ProductTemplateConfigType.CALENDAR_DAY_RESERVATION:
        case ProductTemplateConfigType.CUSTOMER_EXTERNAL:
        case ProductTemplateConfigType.EVENT:
        case ProductTemplateConfigType.GUEST_PERMIT_VALID_TO:
        case ProductTemplateConfigType.GUEST_RESERVATION:
        case ProductTemplateConfigType.GUEST_RESERVATION_UBS:
        case ProductTemplateConfigType.GUEST_DAY_PASS:
        case ProductTemplateConfigType.SUBSCRIPTION:
            return false;

        case ProductTemplateConfigType.PERMIT_UNTIL_REVOCATION:
        case ProductTemplateConfigType.PERMIT_FREE_VALID_TO:
        case ProductTemplateConfigType.PERMIT_FREE_FIXED_DURATION:
            return true;
    }
}

export function OperatorParkingaboUsersProductsTenantRemarkLabel() {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
            }}
        >
            <Localized
                de="Interne Bemerkungen"
                fr="Remarques internes"
                it="Osservazioni interne"
                en="Internal remarks"
            />
            <Tooltip
                title={
                    <Localized
                        de="Die interne Bemerkungen sind nur hier im Parkingportal sichtbar."
                        fr="Les remarques internes sont uniquement visibles ici dans Parkingportal."
                        it="Le osservazioni interne sono visibili unicamente qui in Parkingportal."
                        en="The internal remarks are only visible here in Parkingportal."
                    />
                }
                placement="right"
            >
                <InfoIcon
                    sx={{
                        color: theme => theme.palette.blue.main,
                        marginLeft: 1,
                    }}
                />
            </Tooltip>
        </Box>
    );
}

export function ProductsEnforcementInstructionsLabel() {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
            }}
        >
            <Localized
                de="Anweisungen Kontrolle"
                fr="Instructions contrôle"
                it="Istruzioni controllo"
                en="Enforcement instructions"
            />
            <Tooltip
                title={
                    <Localized
                        de="Diese Anweisungen sind ausschliesslich hier bei Parkingportal sowie bei Parkingcheck (bzw. in den zertifizierten Kontroll-Apps) einsehbar."
                        fr="Ces instructions sont visibles uniquement ici, sur Parkingportal, et sur Parkingcheck (ou dans les applications de contrôle certifiées)."
                        it="Queste istruzioni sono visibili unicamente qui, in Parkingportal, e in Parkingcheck (risp. nelle applicazioni di controllo certificate)."
                        en="These instructions are only available here, on Parkingportal, and on Parkingcheck (or in certified control applications)."
                    />
                }
                placement="right"
            >
                <InfoIcon
                    sx={{
                        color: theme => theme.palette.blue.main,
                        marginLeft: 1,
                    }}
                />
            </Tooltip>
        </Box>
    );
}

function getConcurrentVehiclesLimitConfig(
    config: FreeOfChargeProductTemplateConfig | SubscriptionConfig,
): MaxConcurrentVehiclesLimitConfig | null {
    switch (config.configType) {
        case ProductTemplateConfigType.SUBSCRIPTION:
        case ProductTemplateConfigType.PERMIT_UNTIL_REVOCATION:
        case ProductTemplateConfigType.PERMIT_FREE_FIXED_DURATION:
        case ProductTemplateConfigType.PERMIT_FREE_VALID_TO:
            return config.maxConcurrentVehiclesLimitConfig;
        case ProductTemplateConfigType.ALL_ACCESS:
            return null;
    }
}

export interface ProductsAddTemplateItem {
    contractTemplateId: number;
    productConfig: ProductTemplateConfig;
    zonesDescriptions: string[];
}

enum Fields {
    startDate = 'startDate',
    endDate = 'endDate',
    tenantRemark = 'tenantRemark',
    enforcementInstructions = 'enforcementInstructions',
    durationName = 'durationName',
    maxConcurrentVehicles = 'maxConcurrentVehicles',
}

interface FormFields {
    [Fields.startDate]: string;
    [Fields.endDate]: DateTime | null;
    [Fields.tenantRemark]: string;
    [Fields.enforcementInstructions]: string | null;
    [Fields.durationName]: FixedDurationName | null;
    [Fields.maxConcurrentVehicles]: number | null;
}

enum ContractTemplateIdField {
    contractTemplateId = 'contractTemplateId',
}

interface ContractTemplateIdValue {
    [ContractTemplateIdField.contractTemplateId]: number;
}

interface CreateProductPayload {
    customerNr: string;
    contractTemplateId: number;
    startDate: string;
    endDate: string | null;
    tenantRemark: string;
    enforcementInstructions: string | null;
    duration: FixedDuration | null;
    maxConcurrentVehicles: number | null;
}
